import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  LinearProgress,
  Avatar
} from '@mui/material';
import { Token, Block } from '@mui/icons-material';
import { User, LoginRequest, RegisterRequest } from '../types/api';

// Configure axios defaults
// Use relative URL in production (Docker/nginx), absolute URL in development
axios.defaults.baseURL = process.env.REACT_APP_API_URL;
axios.defaults.timeout = 30000; // Default 30s timeout (can be overridden per request)

// Auth Context Interface
interface AuthContextType {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  login: (credentials: LoginRequest) => Promise<boolean>;
  register: (data: RegisterRequest) => Promise<boolean>;
  logout: () => void;
  updateUser: (userData: Partial<User>) => void;
  checkAuth: () => Promise<boolean>;
}

// Create Context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Auth Provider Component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);
  const [quotaModal, setQuotaModal] = useState<{
    open: boolean;
    used: number;
    limit: number;
    resetsAt: string;
  } | null>(null);

  // Check if user is authenticated
  const isAuthenticated = Boolean(user && token);

  // Logout function (defined early to use in useEffect)
  const logout = () => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    toast.success('Logged out successfully');
  };

  // Set up axios interceptors
  useEffect(() => {
    // Request interceptor to add auth token
    const requestInterceptor = axios.interceptors.request.use(
      (config) => {
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle auth errors
    const responseInterceptor = axios.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401 && token) {
          // Token is invalid, logout user
          logout();
          toast.error('Session expired. Please login again.');
        }
        // Global token quota exceeded handler — shows modal instead of silent failure
        if (
          error.response?.status === 429 &&
          error.response?.data?.code === 'TOKEN_QUOTA_EXCEEDED'
        ) {
          const q = error.response.data.quota || {};
          setQuotaModal({
            open: true,
            used: q.used || 0,
            limit: q.limit || 0,
            resetsAt: q.resets_at || ''
          });
        }
        return Promise.reject(error);
      }
    );

    // Cleanup interceptors
    return () => {
      axios.interceptors.request.eject(requestInterceptor);
      axios.interceptors.response.eject(responseInterceptor);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Check authentication on mount
  useEffect(() => {
    if (token) {
      checkAuth().finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  // Login function
  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      setLoading(true);
      const response = await axios.post('/api/auth/login', credentials);

      // Extract token and user from the backend response structure
      const { tokens, user: userData } = response.data;
      const newToken = tokens?.access_token;

      if (!newToken || !userData) {
        throw new Error('Invalid response from server');
      }

      // Save token to localStorage
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);

      toast.success(`Welcome back, ${userData.email}!`);
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || 'Login failed';
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Register function
  const register = async (data: RegisterRequest): Promise<boolean> => {
    try {
      setLoading(true);

      // Validate passwords match
      if (data.password !== data.confirmPassword) {
        toast.error('Passwords do not match');
        return false;
      }

      const response = await axios.post('/api/auth/register', {
        email: data.email,
        password: data.password,
        confirm_password: data.confirmPassword
      });

      // Extract token and user from the backend response structure
      const { tokens, user: userData } = response.data;
      const newToken = tokens?.access_token;

      if (!newToken || !userData) {
        throw new Error('Invalid response from server');
      }

      // Save token to localStorage
      localStorage.setItem('token', newToken);
      setToken(newToken);
      setUser(userData);

      toast.success(`Account created successfully! Welcome, ${userData.email}!`);
      return true;
    } catch (error: any) {
      const errorMessage =
        error.response?.data?.error || error.response?.data?.message || 'Registration failed';
      toast.error(errorMessage);
      return false;
    } finally {
      setLoading(false);
    }
  };

  // Update user data
  const updateUser = (userData: Partial<User>) => {
    if (user) {
      setUser({ ...user, ...userData });
    }
  };

  // Check if current token is valid
  const checkAuth = async (): Promise<boolean> => {
    try {
      if (!token) {
        return false;
      }

      const response = await axios.get<any>('/api/auth/profile');

      // Backend returns { user: {...}, organization: {...}, api_keys: [...] }
      if (response.data && response.data.user) {
        // Merge organization data into user object if available
        const userData = response.data.user;
        if (response.data.organization) {
          userData.organization = response.data.organization;
        }
        setUser(userData);
        return true;
      } else {
        // Invalid response format
        logout();
        return false;
      }
    } catch (error: any) {
      // Token is invalid or expired
      if (error.response?.status === 401) {
        logout();
      }
      return false;
    }
  };

  // Context value
  const contextValue: AuthContextType = {
    user,
    token,
    isAuthenticated,
    loading,
    login,
    register,
    logout,
    updateUser,
    checkAuth
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
      {/* Global Token Quota Exceeded Modal */}
      <Dialog
        open={!!quotaModal?.open}
        onClose={() => setQuotaModal(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            background: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)',
            p: 3,
            textAlign: 'center'
          }}
        >
          <Avatar
            sx={{ width: 64, height: 64, bgcolor: 'rgba(255,255,255,0.2)', mx: 'auto', mb: 1.5 }}
          >
            <Block sx={{ fontSize: 36, color: '#fff' }} />
          </Avatar>
          <Typography variant="h6" fontWeight={800} color="#fff">
            Monthly Token Limit Reached
          </Typography>
          <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.85)', mt: 0.5 }}>
            Your account has been paused until next month
          </Typography>
        </Box>
        <DialogContent sx={{ pt: 3, pb: 1 }}>
          <Box
            sx={{
              mb: 2,
              p: 2,
              borderRadius: 2,
              bgcolor: 'rgba(238,90,36,0.06)',
              border: '1px solid rgba(238,90,36,0.2)'
            }}
          >
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <Token sx={{ color: 'warning.main', fontSize: 18 }} />
              <Typography variant="body2" fontWeight={700} color="text.primary">
                Token Usage
              </Typography>
            </Box>
            <LinearProgress
              variant="determinate"
              value={100}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'rgba(238,90,36,0.15)',
                '& .MuiLinearProgress-bar': { bgcolor: '#ee5a24' }
              }}
            />
            <Typography
              variant="caption"
              color="error"
              fontWeight={700}
              sx={{ mt: 0.75, display: 'block' }}
            >
              {(quotaModal?.used || 0).toLocaleString()} /{' '}
              {(quotaModal?.limit || 0).toLocaleString()} tokens used
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" textAlign="center">
            All AI processing has been stopped for your account.
            {quotaModal?.resetsAt && (
              <>
                {' '}
                Your quota resets on{' '}
                <strong>
                  {new Date(quotaModal.resetsAt).toLocaleDateString('en-US', {
                    month: 'long',
                    day: 'numeric',
                    year: 'numeric'
                  })}
                </strong>
                .
              </>
            )}
          </Typography>
          <Typography
            variant="caption"
            color="text.disabled"
            display="block"
            textAlign="center"
            mt={1}
          >
            Contact your administrator to increase your limit.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, justifyContent: 'center' }}>
          <Button
            variant="contained"
            onClick={() => setQuotaModal(null)}
            sx={{
              borderRadius: 2,
              fontWeight: 700,
              px: 4,
              bgcolor: '#ee5a24',
              '&:hover': { bgcolor: '#d44000' }
            }}
          >
            Got it
          </Button>
        </DialogActions>
      </Dialog>
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Axios instance with auth - create lazily to avoid test issues
let apiClientInstance: any = null;

export const getApiClient = () => {
  if (!apiClientInstance && typeof window !== 'undefined') {
    apiClientInstance = axios.create({
      baseURL: process.env.REACT_APP_API_URL,
      timeout: 30000
    });

    // Add token to all requests
    apiClientInstance.interceptors.request.use(
      (config: any) => {
        const token = localStorage.getItem('token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error: any) => Promise.reject(error)
    );

    // Handle auth errors globally
    apiClientInstance.interceptors.response.use(
      (response: any) => response,
      (error: any) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('token');
          window.location.href = '/login';
        }
        return Promise.reject(error);
      }
    );
  }
  return apiClientInstance;
};

// Export for backward compatibility - but don't initialize in test environment
export const apiClient = typeof window !== 'undefined' ? getApiClient() : null;

export default AuthContext;
